// ============================================================================
// CHECK-IN GUESTS PANEL (D3 Guest Model Phase A) — shared component
// ============================================================================
// One row per person on the stay: primary guest (fixed row) + accompanying
// guests, each with name/type/age/gender, nationality (COUNTRY enum), state
// (INDIAN_STATE enum when nationality = India) and per-guest ID capture.
// Used by QuickCheckInModal and WalkInModal — the parent owns the row state
// (needed for minimize/dirty snapshots); this component owns the rendering,
// the enum lookups and the shared row helpers/validation exported below.

import React, { useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, FileUploadField } from 'components/ui';
import type { SelectOption } from 'components/ui';
import { enumService } from 'api/services/enumService';
import type { BookingGuest } from 'api/types/booking.types';
import type { CheckInGuestRequest } from 'api/types/checkin.types';
import type { EnumItem } from 'api/types/enum.types';
import type { StoredFileInfo } from 'api/types/fileStorage.types';

// ── Row model ───────────────────────────────────────────────────────────────

export interface GuestRow {
  /** Stable React key — independent of values so editing doesn't remount. */
  key: string;
  /** Existing booking-guest row id — undefined for a newly added guest. */
  id?: string;
  isPrimary: boolean;
  /** Single full-name input; split into first/last on submit. */
  name: string;
  guestType: string;
  /** Kept as string so the number input can be cleared. */
  age: string;
  gender: string;
  nationality: string;
  state: string;
  idType: string;
  idNumber: string;
  phone: string;
  email: string;
  idDocument: StoredFileInfo | null;
}

export const ID_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Aadhar', label: 'Aadhar' },
  { value: 'Passport', label: 'Passport' },
  { value: 'DrivingLicense', label: 'Driving License' },
  { value: 'VoterID', label: 'Voter ID' },
  { value: 'PAN', label: 'PAN' },
  { value: 'GovtID', label: 'Govt ID' },
];

const GENDER_OPTIONS: SelectOption[] = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
];

const GUEST_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Adult', label: 'Adult' },
  { value: 'Child', label: 'Child' },
  { value: 'Infant', label: 'Infant' },
];

// ── Row helpers (shared by both consumers) ──────────────────────────────────

export const createGuestRow = (key: string, overrides?: Partial<GuestRow>): GuestRow => ({
  key,
  isPrimary: false,
  name: '',
  guestType: 'Adult',
  age: '',
  gender: '',
  nationality: 'India',
  state: '',
  idType: '',
  idNumber: '',
  phone: '',
  email: '',
  idDocument: null,
  ...overrides,
});

/** Map a stored booking-guest row into an editable panel row. */
export const toGuestRow = (g: BookingGuest, key: string): GuestRow => ({
  key,
  id: g.id,
  isPrimary: g.isPrimary,
  name: `${g.firstName}${g.lastName ? ` ${g.lastName}` : ''}`.trim(),
  guestType: g.guestType || 'Adult',
  age: g.age != null ? String(g.age) : '',
  gender: g.gender ?? '',
  nationality: g.nationality ?? 'India',
  state: g.state ?? '',
  idType: g.idType ?? '',
  idNumber: g.idNumber ?? '',
  phone: g.phone ?? '',
  email: g.email ?? '',
  idDocument: null,
});

/** Split a single full-name input into the first/last shape the API stores. */
export const splitName = (name: string): { firstName: string; lastName?: string } => {
  const trimmed = name.trim();
  const idx = trimmed.indexOf(' ');
  if (idx < 0) return { firstName: trimmed };
  return { firstName: trimmed.slice(0, idx), lastName: trimmed.slice(idx + 1).trim() || undefined };
};

/** Serialise the editable fields for dirty-tracking snapshots. */
export const serializeGuestRows = (rows: GuestRow[]) =>
  rows.map((g) => ({
    name: g.name,
    guestType: g.guestType,
    age: g.age,
    gender: g.gender,
    nationality: g.nationality,
    state: g.state,
    idType: g.idType,
    idNumber: g.idNumber,
    phone: g.phone,
    email: g.email,
    idDocumentPath: g.idDocument?.relativePath ?? '',
  }));

/** Named rows → the check-in `guests` payload. */
export const buildGuestsPayload = (rows: GuestRow[]): CheckInGuestRequest[] =>
  rows
    .filter((g) => g.name.trim())
    .map((g) => ({
      id: g.id,
      isPrimary: g.isPrimary,
      ...splitName(g.name),
      guestType: g.guestType || 'Adult',
      age: g.age.trim() !== '' ? Number(g.age) : undefined,
      gender: g.gender || undefined,
      nationality: g.nationality.trim() || undefined,
      state: g.state.trim() || undefined,
      idType: g.idType || undefined,
      idNumber: g.idNumber.trim() || undefined,
      phone: g.phone.trim() || undefined,
      email: g.email.trim() || undefined,
      idDocumentPath: g.idDocument?.relativePath || undefined,
    }));

/**
 * Shared validation. Error keys use the same `guest{Field}-{rowKey}` scheme
 * the panel's inputs render.
 */
export const validateGuestRows = (
  rows: GuestRow[],
  opts: { requireAllGuestDetails: boolean; requireIdDocument: boolean },
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const primaryRow = rows.find((g) => g.isPrimary);
  if (primaryRow && !primaryRow.name.trim()) {
    errors[`guestName-${primaryRow.key}`] = 'Primary guest name is required.';
  }
  // Contact details moved onto the guest rows, so the mandatory mobile now sits
  // on the person it belongs to rather than on the booking.
  if (primaryRow && !primaryRow.phone.trim()) {
    errors[`guestPhone-${primaryRow.key}`] = 'Mobile is required for the primary guest.';
  }
  rows.forEach((g) => {
    const blank = !g.name.trim() && !g.idNumber.trim() && !g.idDocument;
    if (blank && !g.isPrimary) return;
    if (!g.name.trim() && !g.isPrimary) errors[`guestName-${g.key}`] = 'Name is required.';
    if (opts.requireAllGuestDetails && (g.name.trim() || g.isPrimary)) {
      if (!g.gender) errors[`guestGender-${g.key}`] = 'Required.';
      if (!g.age.trim()) errors[`guestAge-${g.key}`] = 'Required.';
      if (!g.nationality.trim()) errors[`guestNationality-${g.key}`] = 'Required.';
      if (!g.idType) errors[`guestIdType-${g.key}`] = 'Required.';
      if (!g.idNumber.trim()) errors[`guestIdNumber-${g.key}`] = 'Required.';
    }
  });
  if (opts.requireIdDocument) {
    const hasUpload = rows.some((g) => g.idDocument?.relativePath);
    if (!hasUpload && primaryRow) {
      errors[`guestIdFile-${primaryRow.key}`] =
        'This property requires at least one uploaded guest ID document to check in.';
    }
  }
  return errors;
};

/** Drop a row's error entries for the fields a patch touches. */
export const clearGuestRowErrors = (
  prev: Record<string, string>,
  key: string,
  patch: Partial<Omit<GuestRow, 'key' | 'id' | 'isPrimary'>>,
): Record<string, string> => {
  const next = { ...prev };
  if ('name' in patch)        delete next[`guestName-${key}`];
  if ('gender' in patch)      delete next[`guestGender-${key}`];
  if ('age' in patch)         delete next[`guestAge-${key}`];
  if ('nationality' in patch) delete next[`guestNationality-${key}`];
  if ('idType' in patch)      delete next[`guestIdType-${key}`];
  if ('idNumber' in patch)    delete next[`guestIdNumber-${key}`];
  if ('phone' in patch)       delete next[`guestPhone-${key}`];
  if ('idDocument' in patch)  delete next[`guestIdFile-${key}`];
  return next;
};

// ── Panel component ─────────────────────────────────────────────────────────

interface CheckInGuestsPanelProps {
  guestRows: GuestRow[];
  onUpdateRow: (key: string, patch: Partial<Omit<GuestRow, 'key' | 'id' | 'isPrimary'>>) => void;
  /** Makes one row primary and clears the flag on every other — exactly one always wins. */
  onSetPrimary: (key: string) => void;
  onAddRow: () => void;
  onRemoveRow: (key: string) => void;
  fieldErrors: Record<string, string>;
  submitting: boolean;
  requireAllGuestDetails: boolean;
  /** Booked pax for the "N named · M booked pax" caption; omit to hide. */
  bookedPax?: number;
  /** Optional node under the header — e.g. the pax-mismatch warning. */
  headerExtra?: React.ReactNode;
}

const CheckInGuestsPanel: React.FC<CheckInGuestsPanelProps> = ({
  guestRows, onUpdateRow, onSetPrimary, onAddRow, onRemoveRow,
  fieldErrors, submitting, requireAllGuestDetails, bookedPax, headerExtra,
}) => {
  // Enum lookups cached for the panel's lifetime (config-freshness rules
  // don't apply — country/state lists change ~never within a session).
  const enumCacheRef = useRef<Record<string, EnumItem[]>>({});
  const loadEnumOptions = useCallback(
    (group: string) => async (inputValue: string): Promise<SelectOption[]> => {
      let items = enumCacheRef.current[group];
      if (!items) {
        items = await enumService.getByGroup(group);
        enumCacheRef.current[group] = items;
      }
      const filtered = inputValue
        ? items.filter((i) => i.enumValue.toLowerCase().includes(inputValue.toLowerCase()))
        : items;
      // The display NAME is the stored value (report-compatible; see seed 021).
      return filtered.map((i) => ({ value: i.enumValue, label: i.enumValue }));
    },
    [],
  );
  const loadCountryOptions = useMemo(() => loadEnumOptions('COUNTRY'), [loadEnumOptions]);
  const loadStateOptions = useMemo(() => loadEnumOptions('INDIAN_STATE'), [loadEnumOptions]);

  const namedGuestCount = guestRows.filter((g) => g.name.trim()).length;

  return (
    <div style={{ marginBottom: '0.875rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.375rem', gap: '0.5rem', flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
          Guests{requireAllGuestDetails && <span className="text-danger"> *</span>}
          {bookedPax !== undefined && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
              {namedGuestCount} named · {bookedPax} booked pax
            </span>
          )}
        </h3>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
          {requireAllGuestDetails
            ? 'This property requires full details for every guest'
            : 'Primary guest required · others optional'}
        </span>
      </div>

      {headerExtra}

      {guestRows.map((g) => {
        const isIndia = g.nationality.trim().toLowerCase() === 'india';
        return (
          <div
            key={g.key}
            style={{
              padding: '0.375rem 0.5rem',
              border: `1px solid ${g.isPrimary ? 'rgba(37, 99, 235, 0.4)' : 'var(--border-color)'}`,
              borderRadius: '6px',
              background: 'var(--bg-secondary)',
              marginBottom: '0.375rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.3rem',
            }}
          >
            {/* Sub-row 1 — who: primary, name, type, age, gender (+ remove). */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '28px minmax(0, 2fr) minmax(0, 1fr) 64px minmax(0, 1fr) auto',
              gap: '0.4rem',
              alignItems: 'start',
            }}>
              {/* Primary guest — exactly one, and it is mandatory. Was implicit
                  (first row wins) which meant the desk could not correct it when
                  the lead guest was not entered first. */}
              <button
                type="button"
                onClick={() => onSetPrimary(g.key)}
                disabled={submitting}
                title={g.isPrimary ? 'Primary guest' : 'Mark as primary guest'}
                aria-pressed={g.isPrimary}
                style={{
                  height: '2rem', width: '2rem', borderRadius: '4px', cursor: submitting ? 'default' : 'pointer',
                  border: `1px solid ${g.isPrimary ? 'var(--primary)' : 'var(--form-border-color)'}`,
                  background: g.isPrimary ? 'var(--primary)' : 'transparent',
                  color: g.isPrimary ? '#fff' : 'var(--text-muted)',
                  fontSize: '0.9rem', lineHeight: 1, padding: 0,
                }}
              >
                ★
              </button>
              <div>
                <input
                  type="text"
                  className={`form-input${fieldErrors[`guestName-${g.key}`] ? ' form-field-error' : ''}`}
                  value={g.name}
                  onChange={(e) => onUpdateRow(g.key, { name: e.target.value })}
                  maxLength={100}
                  disabled={submitting}
                  placeholder={g.isPrimary ? 'Primary guest name *' : 'Guest name'}
                />
                {fieldErrors[`guestName-${g.key}`] && (
                  <div className="form-error-message">{fieldErrors[`guestName-${g.key}`]}</div>
                )}
              </div>
              <SyutSelect
                options={GUEST_TYPE_OPTIONS}
                value={g.guestType}
                onChange={(v) => onUpdateRow(g.key, { guestType: (v as string) || 'Adult' })}
                size="sm"
                isDisabled={submitting}
                menuPortalTarget={document.body}
                placeholder="Type"
              />
              <div>
                <input
                  type="number"
                  min={0}
                  max={120}
                  className={`form-input${fieldErrors[`guestAge-${g.key}`] ? ' form-field-error' : ''}`}
                  value={g.age}
                  onChange={(e) => onUpdateRow(g.key, { age: e.target.value })}
                  disabled={submitting}
                  placeholder="Age"
                />
                {fieldErrors[`guestAge-${g.key}`] && (
                  <div className="form-error-message">{fieldErrors[`guestAge-${g.key}`]}</div>
                )}
              </div>
              <SyutSelect
                options={GENDER_OPTIONS}
                value={g.gender}
                onChange={(v) => onUpdateRow(g.key, { gender: (v as string) || '' })}
                isClearable
                size="sm"
                error={fieldErrors[`guestGender-${g.key}`]}
                isDisabled={submitting}
                menuPortalTarget={document.body}
                placeholder="Gender"
              />
              {g.isPrimary ? (
                <span style={{
                  alignSelf: 'center', padding: '0.1rem 0.45rem', borderRadius: '999px',
                  fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap',
                  background: 'rgba(37, 99, 235, 0.12)', color: '#1d4ed8', border: '1px solid rgba(37, 99, 235, 0.35)',
                }}>
                  Primary
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => onRemoveRow(g.key)}
                  disabled={submitting}
                  style={{ padding: '0.25rem 0.4rem', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center' }}
                  title="Remove this guest"
                  aria-label="Remove guest"
                >
                  <IconSystem.x size={14} />
                </button>
              )}
            </div>

            {/* Sub-row 2 — where from: nationality + state (India → seeded list). */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.4rem' }}>
              <SyutSelect
                isAsync
                loadOptions={loadCountryOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={g.nationality}
                onChange={(v) => onUpdateRow(g.key, {
                  nationality: (v as string) || '',
                  // Country change invalidates a previously picked state.
                  state: (v as string)?.trim().toLowerCase() === 'india' ? g.state : '',
                })}
                size="sm"
                error={fieldErrors[`guestNationality-${g.key}`]}
                isDisabled={submitting}
                menuPortalTarget={document.body}
                placeholder="Nationality"
              />
              {isIndia ? (
                <SyutSelect
                  isAsync
                  loadOptions={loadStateOptions}
                  defaultOptions={true}
                  cacheOptions={false}
                  value={g.state}
                  onChange={(v) => onUpdateRow(g.key, { state: (v as string) || '' })}
                  isClearable
                  size="sm"
                  isDisabled={submitting}
                  menuPortalTarget={document.body}
                  placeholder="State"
                />
              ) : (
                <input
                  type="text"
                  className="form-input"
                  value={g.state}
                  onChange={(e) => onUpdateRow(g.key, { state: e.target.value })}
                  maxLength={100}
                  disabled={submitting}
                  placeholder="State / province (optional)"
                />
              )}
            </div>

            {/* Sub-row 2b — how to reach THIS guest. Contact details belong to a
                person, not a stay: a four-room booking needs a number for each
                party, not one for the whole booking. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.3fr)', gap: '0.4rem' }}>
              <div>
                <input
                  type="tel"
                  className={`form-input${fieldErrors[`guestPhone-${g.key}`] ? ' form-field-error' : ''}`}
                  value={g.phone}
                  onChange={(e) => onUpdateRow(g.key, { phone: e.target.value })}
                  maxLength={20}
                  disabled={submitting}
                  placeholder={g.isPrimary ? 'Mobile *' : 'Mobile'}
                />
                {fieldErrors[`guestPhone-${g.key}`] && (
                  <div className="form-error-message">{fieldErrors[`guestPhone-${g.key}`]}</div>
                )}
              </div>
              <input
                type="email"
                className="form-input"
                value={g.email}
                onChange={(e) => onUpdateRow(g.key, { email: e.target.value })}
                maxLength={100}
                disabled={submitting}
                placeholder="Email"
              />
            </div>

            {/* Sub-row 3 — ID: type + number + optional upload (decision 6). */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.4rem' }}>
              <SyutSelect
                options={ID_TYPE_OPTIONS}
                value={g.idType}
                onChange={(v) => onUpdateRow(g.key, { idType: (v as string) || '' })}
                isClearable
                size="sm"
                error={fieldErrors[`guestIdType-${g.key}`]}
                isDisabled={submitting}
                menuPortalTarget={document.body}
                placeholder="ID Type"
              />
              <div>
                <input
                  type="text"
                  className={`form-input${fieldErrors[`guestIdNumber-${g.key}`] ? ' form-field-error' : ''}`}
                  value={g.idNumber}
                  onChange={(e) => onUpdateRow(g.key, { idNumber: e.target.value })}
                  maxLength={50}
                  disabled={submitting}
                  placeholder="ID Number"
                />
                {fieldErrors[`guestIdNumber-${g.key}`] && (
                  <div className="form-error-message">{fieldErrors[`guestIdNumber-${g.key}`]}</div>
                )}
              </div>
            </div>
            <FileUploadField
              compact
              category="guest_document"
              value={g.idDocument}
              onChange={(info) => onUpdateRow(g.key, { idDocument: info })}
              disabled={submitting || !g.idType.trim()}
              error={fieldErrors[`guestIdFile-${g.key}`]}
            />
          </div>
        );
      })}

      <button
        type="button"
        className="btn btn-secondary"
        onClick={onAddRow}
        disabled={submitting}
        style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
      >
        <IconSystem.add size={14} /> Add guest
      </button>
    </div>
  );
};

export default CheckInGuestsPanel;
